import React from "react";

// Animated decorations for a seasonal theme (see data/seasons.js): falling
// snow, rising hearts, bursting fireworks… Drawn with plain elements and CSS,
// purely decorative, and still for people who ask their device for less
// motion.
const rand = (seed) => {
  const value = Math.sin(seed * 12.9898) * 43758.5453;
  return value - Math.floor(value);
};

// What each effect is made of, and how many pieces it uses.
const PIECES = {
  snow: { count: 40, glyphs: ["❄", "❅", "•", "•"] },
  hearts: { count: 22, glyphs: ["♥", "❤", "💗"] },
  eggs: { count: 18, glyphs: ["🥚", "🐣", "🌷", "🐰"] },
  flags: {
    count: 34,
    glyphs: null,
    colors: ["#007a4d", "#ffb612", "#de3831", "#002395", "#000000", "#ffffff"],
  },
  flowers: { count: 20, glyphs: ["🌸", "🌷", "🌼", "💐"] },
  stars: { count: 26, glyphs: ["★", "✦", "✧"] },
  leaves: { count: 22, glyphs: ["🍂", "🍁", "✦"] },
  bats: { count: 14, glyphs: ["🦇", "🎃", "👻"] },
  tags: { count: 18, glyphs: ["🏷️", "✦", "%"] },
  fireworks: { count: 7, glyphs: null },
};

// How each effect moves (CSS animation in styles/seasons.scss).
const MOTION = {
  snow: "fall",
  hearts: "rise",
  eggs: "fall",
  flags: "fall",
  flowers: "fall",
  stars: "twinkle",
  leaves: "fall",
  bats: "flutter",
  tags: "fall",
  fireworks: "burst",
};

export default function SeasonalEffects({ effect, className = "", density = 1 }) {
  const kind = PIECES[effect];
  if (!kind) return null;
  const motion = MOTION[effect];

  return (
    <div
      className={`season-fx season-fx--${motion} season-fx--${effect} ${className}`}
      aria-hidden="true"
    >
      {Array.from({ length: Math.max(3, Math.round(kind.count * density)) }, (_, index) => {
        const seed = index + 1;
        const style = {
          "--x": `${(rand(seed) * 100).toFixed(1)}%`,
          "--y": `${(rand(seed + 30) * 80).toFixed(1)}%`,
          "--size": (0.6 + rand(seed + 60) * 0.9).toFixed(2),
          "--delay": `${(-rand(seed + 90) * 14).toFixed(1)}s`,
          "--time": `${(7 + rand(seed + 120) * 9).toFixed(1)}s`,
          "--drift": `${((rand(seed + 150) - 0.5) * 120).toFixed(0)}px`,
          "--hue": `${Math.round(rand(seed + 180) * 360)}deg`,
        };
        if (kind.colors) {
          return (
            <span
              key={index}
              className="season-fx__chip"
              style={{
                ...style,
                background: kind.colors[index % kind.colors.length],
              }}
            />
          );
        }
        if (!kind.glyphs) {
          // Fireworks: a burst of sparks around a point.
          return (
            <span key={index} className="season-fx__burst" style={style}>
              {Array.from({ length: 12 }, (_, spark) => (
                <i key={spark} style={{ "--angle": `${spark * 30}deg` }} />
              ))}
            </span>
          );
        }
        return (
          <span key={index} className="season-fx__piece" style={style}>
            {kind.glyphs[index % kind.glyphs.length]}
          </span>
        );
      })}
    </div>
  );
}
